import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { versionAPI } from '../../services/api';

function VersionInfo() {
  const [backendVersion, setBackendVersion] = useState(null);
  const [frontendVersion, setFrontendVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadVersionInfo();
  }, []);

  const loadVersionInfo = async () => {
    try {
      setLoading(true);
      const [backendRes, frontendRes] = await Promise.all([
        versionAPI.getBackendVersion(),
        versionAPI.getFrontendVersion()
      ]);

      setBackendVersion(backendRes.data);
      setFrontendVersion(frontendRes.data);
    } catch (error) {
      console.error('Failed to load version info:', error);
      setError('Failed to load version information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="fade-in">
        <Card.Header>📦 Version Information</Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading version info...</span>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="fade-in">
        <Card.Header>📦 Version Information</Card.Header>
        <Card.Body>
          <Alert variant="warning">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'unknown') return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Card className="fade-in">
      <Card.Header>
        📦 Version Information
        <small className="text-muted ms-2">Deployment tracking</small>
      </Card.Header>
      <Card.Body>
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>Component</th>
              <th style={{ width: '20%' }}>Version</th>
              <th style={{ width: '20%' }}>Git Commit</th>
              <th style={{ width: '15%' }}>Branch</th>
              <th style={{ width: '20%' }}>Build Time</th>
            </tr>
          </thead>
          <tbody>
            {/* Backend Version */}
            <tr>
              <td>
                <strong>Backend (API)</strong>
                <br />
                <small className="text-muted">Node {backendVersion?.nodeVersion}</small>
              </td>
              <td>
                <Badge bg="info">{backendVersion?.version || 'unknown'}</Badge>
              </td>
              <td>
                <code style={{ fontSize: '0.85rem' }}>
                  {backendVersion?.gitHash || 'unknown'}
                </code>
                {backendVersion?.gitHash !== 'unknown' && (
                  <a 
                    href={`https://github.com/YOUR_GITHUB_USERNAME/YOUR_REPO_NAME/commit/${backendVersion?.gitHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-2"
                    style={{ fontSize: '0.75rem' }}
                  >
                    View
                  </a>
                )}
              </td>
              <td>
                <Badge bg="secondary" className="font-monospace">
                  {backendVersion?.gitBranch || 'unknown'}
                </Badge>
              </td>
              <td>
                <small>{formatDate(backendVersion?.buildTime)}</small>
              </td>
            </tr>

            {/* Frontend Version */}
            <tr>
              <td>
                <strong>Frontend (UI)</strong>
                <br />
                <small className="text-muted">React Application</small>
              </td>
              <td>
                <Badge bg="info">{frontendVersion?.version || 'unknown'}</Badge>
              </td>
              <td>
                <code style={{ fontSize: '0.85rem' }}>
                  {frontendVersion?.gitHash || 'unknown'}
                </code>
                {frontendVersion?.gitHash !== 'unknown' && (
                  <a 
                    href={`https://github.com/YOUR_GITHUB_USERNAME/YOUR_REPO_NAME/commit/${frontendVersion?.gitHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-2"
                    style={{ fontSize: '0.75rem' }}
                  >
                    View
                  </a>
                )}
              </td>
              <td>
                <Badge bg="secondary" className="font-monospace">
                  {frontendVersion?.gitBranch || 'unknown'}
                </Badge>
              </td>
              <td>
                <small>{formatDate(frontendVersion?.buildTime)}</small>
              </td>
            </tr>
          </tbody>
        </Table>

        <Alert variant="info" className="mb-0 mt-3">
          <small>
            <strong>How to use:</strong> Compare git hashes to see if frontend and backend are in sync.
            If they differ significantly, you may need to deploy one or both services.
          </small>
        </Alert>

        {backendVersion?.environment && (
          <Alert variant={backendVersion.environment === 'production' ? 'success' : 'warning'} className="mb-0 mt-2">
            <small>
              <strong>Environment:</strong> {backendVersion.environment}
            </small>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}

export default VersionInfo;
